import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Field, Form, FormikProvider, useFormik } from 'formik'
import * as Yup from 'yup'
import JSZip from 'jszip'
import CustomFile from '../../../components/common/CustomFile'
import CustomSelect from '../../../components/common/CustomSelect'
import axiosAuthInstance from '../../../utils/axios/axiosAuthInstance'
import axiosCommonInstance from '../../../utils/axios/axiosCommonInstance'

const validationSchema = Yup.object({
  company: Yup.string().required('Company is required'),
  category: Yup.string().required('Category is required'),
})

const Edit = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [companyOptions, setCompanyOptions] = useState([])
  const [categoryOptions, setCategoryOptions] = useState([])
  const [filePreviews, setFilePreviews] = useState({})
  const [files, setFiles] = useState([])
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isExtracting, setIsExtracting] = useState(false)
  const [error, setError] = useState(null)
  const [sharedImageSize, setSharedImageSize] = useState({
    width: 0,
    height: 0,
  })
  const [sharedObjects, setSharedObjects] = useState([
    {
      name: 'Object 1',
      position: { x: 0, y: 0, z: 0 },
      rotation: { x: 0, y: 0, z: 0 },
      scale: { x: 1, y: 1, z: 1 },
    },
  ])
  const [loading, setLoading] = useState(true)

  const fetchCompanyData = useCallback(async () => {
    try {
      const response = await axiosAuthInstance.get('company/dropdown')
      if (response?.status === 200) {
        setCompanyOptions(
          response.data.result.map((item) => ({
            value: item._id,
            label: item.name,
          })),
        )
      }
    } catch (error) {
      setError('Failed to load company data')
    }
  }, [])

  const fetchCategoryData = useCallback(async () => {
    try {
      const response = await axiosCommonInstance.get(
        'library-category/dropdown',
      )
      if (response?.status === 200) {
        setCategoryOptions(
          response.data.result.map((item) => ({
            value: item._id,
            label: item.name,
          })),
        )
      }
    } catch (error) {
      setError('Failed to load category data')
    }
  }, [])

  const fetchLibraryData = useCallback(async () => {
    try {
      const response = await axiosAuthInstance.get(`library/detail/${id}`)
      const libraryData = response.data.result

      if (libraryData) {
        formik.setValues({
          company: libraryData.company?._id || '',
          category: libraryData.category?._id || '',
        })

        const roomData = libraryData.RoomData
        setFilePreviews({
          Master: roomData.Master,
          Specular: roomData.Specular,
          Reflection: roomData.Reflection,
          Lighting: roomData.Lighting,
          Extratex: roomData.Extratex,
        })

        setSharedObjects(
          roomData.RoomCoordinate || [
            {
              name: 'Object 1',
              position: { x: 0, y: 0, z: 0 },
              rotation: { x: 0, y: 0, z: 0 },
              scale: { x: 1, y: 1, z: 1 },
            },
          ],
        )

        setSharedImageSize(roomData.ImageSize || { width: 0, height: 0 })
      }
    } catch (error) {
      setError('Failed to load library data')
    } finally {
      setLoading(false)
    }
  }, [id])

  const extractImagesFromZip = async (zipFile) => {
    try {
      setIsExtracting(true);
      const zip = new JSZip();
      const zipContent = await zip.loadAsync(zipFile);
      const extractedFiles = [];
  
      const getMimeType = (filename) => {
        const extension = filename.split('.').pop().toLowerCase();
        switch (extension) {
          case 'jpg':
          case 'jpeg': return 'image/jpeg';
          case 'png': return 'image/png';
          case 'gif': return 'image/gif';
          case 'webp': return 'image/webp';
          default: return 'application/octet-stream';
        }
      };
  

      for (const [relativePath, zipEntry] of Object.entries(zipContent.files)) {

        if (!zipEntry.dir && /\.(jpe?g|png|gif|webp)$/i.test(relativePath)) {
          const fileName = relativePath.split('/').pop();
          const fieldMatch = fileName.match(/(Master|Specular|Reflection|Lighting|Extratex)/i);
          
          if (fieldMatch) {
            const field = fieldMatch[0].charAt(0).toUpperCase() + 
                         fieldMatch[0].slice(1).toLowerCase();
            
            const arrayBuffer = await zipEntry.async('arraybuffer');
            const file = new File([arrayBuffer], fileName, {
              type: getMimeType(fileName),
              lastModified: Date.now(),
            });
  
            extractedFiles.push({ field, file });
          }
        }
      }
  
      if (extractedFiles.length === 0) {
        throw new Error('No valid images found in ZIP');
      }
  
      return extractedFiles;
    } catch (error) {
      throw new Error(`Failed to extract ZIP: ${error.message}`);
    } finally {
      setIsExtracting(false);
    }
  };
  
  const handleFileChange = async (event) => {
    setError(null);
    const selectedFiles = Array.from(event.target.files);
    if (selectedFiles.length === 0) return;
  
    try {
      const processedFiles = [];
      const previews = {};
  
      for (const file of selectedFiles) {
        const isZip = file.type === 'application/zip' || 
                     file.type === 'application/x-zip-compressed' || 
                     file.name.toLowerCase().endsWith('.zip');
  
        if (isZip) {
          const extracted = await extractImagesFromZip(file);
          processedFiles.push(...extracted);
          extracted.forEach(({ field, file: f }) => {
            previews[field] = URL.createObjectURL(f);
          });
        } else if (file.type.startsWith('image/')) {
          const fieldMatch = file.name.match(/(Master|Specular|Reflection|Lighting|Extratex)/i);
          const field = fieldMatch ? 
            fieldMatch[0].charAt(0).toUpperCase() + fieldMatch[0].slice(1).toLowerCase() : 
            null;
          
          processedFiles.push({ field, file });
          if (field) {
            previews[field] = URL.createObjectURL(file);
          }
        }
      }
  
      if (processedFiles.length === 0) {
        throw new Error('No valid images found. Supported types: Master, Specular, Reflection, Lighting, Extratex');
      }
  
      setFiles(processedFiles);
      setFilePreviews(prev => ({ ...prev, ...previews }));
      formik.setFieldValue('files', processedFiles);
    } catch (error) {
      setError(error.message);
      formik.setFieldValue('files', []);
    }
  };
  
  const handleSubmit = async (values) => {
    setIsSubmitted(true);
    setError(null);
  
    try {
      const requiredFields = ['Master', 'Specular', 'Reflection', 'Lighting', 'Extratex'];
      const missingFields = requiredFields.filter(field => !filePreviews[field]);
      
      if (missingFields.length > 0) {
        throw new Error(`Missing required images: ${missingFields.join(', ')}`);
      }
  
      const formData = new FormData();
      formData.append('company', values.company);
      formData.append('category', values.category);
  
      files.forEach(({ field, file }) => {
        if (field && filePreviews[field]) {
          formData.append(field, file);
        }
      });
  
      Object.entries(filePreviews).forEach(([field, url]) => {
        if (!files.some(f => f.field === field) && typeof url === 'string') {
          formData.append(field, url);
        }
      });
  
      formData.append('ImageSize', JSON.stringify(sharedImageSize));
      formData.append('RoomCoordinate', JSON.stringify(sharedObjects));
  
      const response = await axiosAuthInstance.put(
        `library/update/${id}`,
        formData
      ).catch(error => {
        if (error.response) {
          throw new Error(error.response.data.message || 'Server responded with an error');
        } else if (error.request) {
          throw new Error('No response received from server');
        } else {
          throw new Error('Error setting up request');
        }
      });
  
      if (response && response.status === 200) {
        navigate('/library');
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (error) {
      setError(error.message || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitted(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      company: '',
      category: '',
    },
    validationSchema,
    onSubmit: handleSubmit,
  })

  const updateImageSize = (field, value) => {
    setSharedImageSize((prev) => ({
      ...prev,
      [field]: parseFloat(value) || 0,
    }))
  }

  const updateObject = (objIndex, field, subField, value) => {
    const updated = [...sharedObjects]
    if (field === 'name') {
      updated[objIndex].name = value
    } else {
      updated[objIndex][field][subField] =
        parseFloat(value) || (field === 'scale' ? 1 : 0)
    }
    setSharedObjects(updated)
  }

  const addObject = () => {
    setSharedObjects((prev) => [
      ...prev,
      {
        name: `Object ${prev.length + 1}`,
        position: { x: 0, y: 0, z: 0 },
        rotation: { x: 0, y: 0, z: 0 },
        scale: { x: 1, y: 1, z: 1 },
      },
    ])
  }

  const removeObject = (index) => {
    if (sharedObjects.length <= 1) return
    const updated = [...sharedObjects]
    updated.splice(index, 1)
    setSharedObjects(updated)
  }

  useEffect(() => {
    fetchCompanyData()
    fetchCategoryData()
    fetchLibraryData()
    return () => {
      Object.values(filePreviews).forEach((url) => {
        if (typeof url === 'string') URL.revokeObjectURL(url)
      })
    }
  }, [fetchCompanyData, fetchCategoryData, fetchLibraryData])

  if (loading) return <div className='container mx-auto p-4'>Loading...</div>

  return (
    <div className='container mx-auto p-4'>
      <FormikProvider value={formik}>
        <Form
          className='space-y-4 p-4 bg-white rounded-lg shadow'
          onSubmit={formik.handleSubmit}
        >
          <h1 className='text-2xl font-bold mb-6'>Edit Library</h1>

          {error && (
            <div className='bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4'>
              <p className='font-bold'>Error</p>
              <p>{error}</p>
            </div>
          )}

          <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
            <div>
              <Field
                name='company'
                label='Company'
                component={CustomSelect}
                options={companyOptions}
                required
              />
              <Field
                name='category'
                label='Category'
                component={CustomSelect}
                options={categoryOptions}
                required
              />

              <div className='mt-6'>
                <Field
                  name='file'
                  label='Update Images (ZIP or individual files)'
                  component={CustomFile}
                  multiple
                  accept='image/*,.zip'
                  onChange={handleFileChange}
                />
                {formik.touched.files && formik.errors.files ? (
                  <div className='text-red-500 text-sm mt-1'>
                    {formik.errors.files}
                  </div>
                ) : null}
                {isExtracting && (
                  <div className='flex items-center text-blue-600 mt-2'>
                    Processing files...
                  </div>
                )}
              </div>

              {Object.keys(filePreviews).length > 0 && (
                <div className='mt-6'>
                  <h3 className='text-lg font-semibold mb-2'>Current Images</h3>
                  <div className='grid grid-cols-2 md:grid-cols-3 gap-4'>
                    {Object.entries(filePreviews).map(([field, url]) => (
                      <div
                        key={field}
                        className='border rounded overflow-hidden'
                      >
                        <div className='bg-gray-100 p-2 text-sm font-medium'>
                          {field}
                        </div>
                        <img
                          src={url}
                          className='w-full h-32 object-cover'
                          alt={field}
                          onLoad={() => {
                            if (
                              typeof url === 'string' &&
                              url.startsWith('blob:')
                            ) {
                              URL.revokeObjectURL(url)
                            }
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div>
              <div className='border p-4 rounded space-y-4'>
                <h3 className='text-lg font-semibold'>Shared Image Size</h3>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      Width
                    </label>
                    <input
                      type='number'
                      value={sharedImageSize.width}
                      onChange={(e) => updateImageSize('width', e.target.value)}
                      className='w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    />
                  </div>
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>
                      Height
                    </label>
                    <input
                      type='number'
                      value={sharedImageSize.height}
                      onChange={(e) =>
                        updateImageSize('height', e.target.value)
                      }
                      className='w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                    />
                  </div>
                </div>

                <h3 className='text-lg font-semibold mt-6'>
                  Shared Object Coordinates
                </h3>
                {sharedObjects.map((obj, objIndex) => (
                  <div key={objIndex} className='border p-4 rounded space-y-3'>
                    <div className='flex justify-between items-center'>
                      <h4 className='font-medium'>
                        {obj.name || `Object ${objIndex + 1}`}
                      </h4>
                      {sharedObjects.length > 1 && (
                        <button
                          type='button'
                          onClick={() => removeObject(objIndex)}
                          className='text-red-500 hover:text-red-700 text-sm'
                        >
                          Remove
                        </button>
                      )}
                    </div>
                    <div>
                      <label className='block text-sm font-medium text-gray-700 mb-1'>
                        Name
                      </label>
                      <input
                        type='text'
                        className='w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                        value={obj.name}
                        onChange={(e) =>
                          updateObject(objIndex, 'name', null, e.target.value)
                        }
                      />
                    </div>
                    {['position', 'rotation', 'scale'].map((type) => (
                      <div key={type} className='space-y-1'>
                        <label className='block text-sm font-medium text-gray-700 capitalize'>
                          {type}
                        </label>
                        <div className='grid grid-cols-3 gap-2'>
                          {['x', 'y', 'z'].map((axis) => (
                            <div key={`${type}-${axis}`}>
                              <label className='block text-xs text-gray-500 uppercase'>
                                {axis}
                              </label>
                              <input
                                type='number'
                                step={type === 'scale' ? '0.1' : '1'}
                                className='w-full border p-2 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500'
                                value={obj[type][axis]}
                                onChange={(e) =>
                                  updateObject(
                                    objIndex,
                                    type,
                                    axis,
                                    e.target.value,
                                  )
                                }
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
                <button
                  type='button'
                  onClick={addObject}
                  className='text-white bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded text-sm'
                >
                  + Add Object
                </button>
              </div>
            </div>
          </div>

          <div className='flex justify-end mt-6'>
            <button
              type='submit'
              disabled={isSubmitted || isExtracting || !formik.isValid}
              className={`px-6 py-2 rounded text-white ${isSubmitted || isExtracting || !formik.isValid ? 'bg-orange-400 cursor-not-allowed' : 'bg-orange-500 hover:bg-orange-600'}`}
            >
              {isSubmitted
                ? 'Updating...'
                : isExtracting
                  ? 'Processing...'
                  : 'Update'}
            </button>
          </div>
        </Form>
      </FormikProvider>
    </div>
  )
}

export default Edit
